import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Minus, SlidersHorizontal } from "lucide-react";

export const removeFeeAdjustment = async (appId, line, onSaved) => {
  if (!window.confirm(`Remove "${line.label}"? A note of the change is saved.`)) return;
  try { await api.delete(`/applications/${appId}/fee-adjustments/${line.id}`); toast.success("Adjustment removed"); onSaved(); }
  catch (e) { toast.error(apiErr(e)); }
};

// Case-only fee adjustment (+/−) with a reason; never changes the master fee list. Saves a fee-change note on the case and client.
export function FeeAdjustmentForm({ appId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sign, setSign] = useState(1);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const n = parseFloat(amount);
    if (!(n > 0)) { toast.error("Enter an amount greater than 0"); return; }
    if (!reason.trim()) { toast.error("Please give a reason for the fee change"); return; }
    setBusy(true);
    try {
      await api.post(`/applications/${appId}/fee-adjustments`, { amount: sign * n, reason: reason.trim() });
      toast.success("Fee adjusted for this case — note saved");
      setAmount(""); setReason(""); setOpen(false); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  if (!open) return (
    <button data-testid="fee-adjust-open" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
      <SlidersHorizontal className="h-3.5 w-3.5" /> Adjust fee for this case
    </button>
  );
  return (
    <div data-testid="fee-adjust-form" className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Fee adjustment · this case only (master list unchanged)</div>
      <div className="flex flex-wrap gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          <button data-testid="fee-adjust-add" onClick={() => setSign(1)} className={`px-2.5 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1 ${sign > 0 ? "bg-emerald-600 text-white" : "text-slate-500"}`}><Plus className="h-3 w-3" /> Add</button>
          <button data-testid="fee-adjust-subtract" onClick={() => setSign(-1)} className={`px-2.5 py-1.5 rounded-md text-xs font-semibold inline-flex items-center gap-1 ${sign < 0 ? "bg-rose-600 text-white" : "text-slate-500"}`}><Minus className="h-3 w-3" /> Take off</button>
        </div>
        <input data-testid="fee-adjust-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className="w-28 px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
        <input data-testid="fee-adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} placeholder="Reason (e.g. extra spouse forms, goodwill)" className="flex-1 min-w-[180px] px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm" />
      </div>
      <div className="flex gap-2 justify-end">
        <button data-testid="fee-adjust-cancel" onClick={() => setOpen(false)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold">Cancel</button>
        <button data-testid="fee-adjust-save" onClick={save} disabled={busy} className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-semibold disabled:opacity-60">{busy ? "Saving…" : "Save adjustment"}</button>
      </div>
    </div>
  );
}
